import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { StatsCards } from "@/components/dashboard/StatsCards";
import { DemandChart, SalesSummary } from "@/components/dashboard/ChartsSection";
import { ReorderSuggestions } from "@/components/dashboard/ReorderSuggestions";
import { LowStockAlerts } from "@/components/dashboard/LowStockAlerts";
import { PendingPOs } from "@/components/dashboard/PendingPOs";
import { CreatePODialog, ViewPODialog } from "@/components/dashboard/PODialogs";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, hasPerm } = useAuth();
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [demandAnalysis, setDemandAnalysis] = useState([]);
  const [reorderSuggestions, setReorderSuggestions] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // PO Modal State
  const [poDialog, setPODialog] = useState(false);
  const [poItems, setPOItems] = useState([]);
  const [supplierName, setSupplierName] = useState("");
  const [poNotes, setPONotes] = useState("");
  
  // View/Edit PO Modal
  const [viewPODialog, setViewPODialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [editingPO, setEditingPO] = useState(false);
  const [editPOItems, setEditPOItems] = useState([]);
  const [editSupplierName, setEditSupplierName] = useState("");
  const [editPONotes, setEditPONotes] = useState("");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/inventory/low-stock`),
        axios.get(`${API_URL}/dashboard/demand-analysis`),
        axios.get(`${API_URL}/inventory/reorder-suggestions`),
      ];
      if (user?.role === 'admin') {
        requests.push(axios.get(`${API_URL}/purchase-orders/pending`));
      }
      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setLowStockItems(responses[1].data);
      setDemandAnalysis(responses[2].data);
      setReorderSuggestions(responses[3].data);
      if (user?.role === 'admin' && responses[4]) {
        setPendingPOs(responses[4].data);
      }
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const openPOModal = (items, type = 'reorder') => {
    const poItemsData = items.map(item => ({
      part_number: item.part_number,
      name: type === 'reorder' ? item.item_name : item.name,
      brand: item.brand || null,
      category: item.category || null,
      quantity: type === 'reorder' ? item.recommended_order_quantity : Math.max(10, item.reorder_level - item.quantity + 10),
      urgency: type === 'reorder' ? item.urgency : (item.quantity === 0 ? 'critical' : 'high')
    }));
    const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    poItemsData.sort((a, b) => (urgencyOrder[a.urgency] || 2) - (urgencyOrder[b.urgency] || 2));
    setPOItems(poItemsData);
    setSupplierName("");
    setPONotes("");
    setPODialog(true);
  };

  const updatePOItem = (index, field, value) => {
    const updated = [...poItems];
    updated[index][field] = field === 'quantity' ? parseInt(value) || 0 : value;
    setPOItems(updated);
  };

  const removePOItem = (index) => setPOItems(poItems.filter((_, i) => i !== index));

  const handleCreatePO = async () => {
    if (poItems.length === 0) { toast.error("Please add at least one item"); return; }
    try {
      await axios.post(`${API_URL}/purchase-orders`, { supplier_name: supplierName.trim() || null, items: poItems, notes: poNotes || null });
      toast.success(user?.role === 'admin' ? "Purchase Order created and approved!" : "Purchase Order submitted for approval!");
      setPODialog(false);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to create Purchase Order");
    }
  };

  const openViewPOModal = (po) => {
    setSelectedPO(po);
    setEditingPO(false);
    setEditPOItems(po.items.map(item => ({ ...item })));
    setEditSupplierName(po.supplier_name);
    setEditPONotes(po.notes || "");
    setViewPODialog(true);
  };

  const handleUpdatePO = async () => {
    try {
      await axios.put(`${API_URL}/purchase-orders/${selectedPO.id}`, { supplier_name: editSupplierName.trim() || null, items: editPOItems, notes: editPONotes || null });
      toast.success("Purchase Order updated!");
      setViewPODialog(false);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update Purchase Order");
    }
  };

  const handleApprovePO = async (poId) => {
    try { await axios.post(`${API_URL}/purchase-orders/${poId}/approve`); toast.success("Purchase Order approved!"); setViewPODialog(false); fetchDashboardData(); }
    catch (error) { toast.error("Failed to approve Purchase Order"); }
  };

  const handleRejectPO = async (poId) => {
    try { await axios.post(`${API_URL}/purchase-orders/${poId}/reject`); toast.success("Purchase Order rejected"); setViewPODialog(false); fetchDashboardData(); }
    catch (error) { toast.error("Failed to reject Purchase Order"); }
  };

  const handleDeletePO = async (poId) => {
    if (!window.confirm("Are you sure you want to delete this Purchase Order?")) return;
    try { await axios.delete(`${API_URL}/purchase-orders/${poId}`); toast.success("Purchase Order deleted"); setViewPODialog(false); fetchDashboardData(); }
    catch (error) { toast.error("Failed to delete Purchase Order"); }
  };

  const handleExportPO = async (poId, format) => {
    try {
      const response = await axios.get(`${API_URL}/purchase-orders/${poId}/export/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || `purchase_order.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Purchase Order exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export PO as ${format.toUpperCase()}`);
    }
  };

  const updateEditPOItem = (index, field, value) => {
    const updated = [...editPOItems];
    updated[index][field] = field === 'quantity' ? parseInt(value) || 0 : value;
    setEditPOItems(updated);
  };

  const removeEditPOItem = (index) => setEditPOItems(editPOItems.filter((_, i) => i !== index));

  if (loading) {
    return <div className="p-8"><div className="text-slate-500">Loading dashboard...</div></div>;
  }

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>Dashboard</h1>
        <p className="text-slate-500 mt-1">Welcome back, {user?.name}</p>
      </div>

      <StatsCards stats={stats} user={user} pendingPOsCount={pendingPOs.length} />

      {user?.role === 'admin' && (
        <PendingPOs pendingPOs={pendingPOs} onView={openViewPOModal} onApprove={handleApprovePO} onReject={handleRejectPO} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 grid grid-cols-1 gap-6">
          <DemandChart demandAnalysis={demandAnalysis} />
        </div>
        <ActivityFeed />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <SalesSummary demandAnalysis={demandAnalysis} />
      </div>

      <ReorderSuggestions suggestions={reorderSuggestions} onGeneratePO={openPOModal} />
      <LowStockAlerts items={lowStockItems} onGeneratePO={openPOModal} />

      <CreatePODialog
        open={poDialog} onOpenChange={setPODialog} poItems={poItems} supplierName={supplierName}
        poNotes={poNotes} user={user} onSupplierChange={setSupplierName} onNotesChange={setPONotes}
        onUpdateItem={updatePOItem} onRemoveItem={removePOItem} onSubmit={handleCreatePO}
      />

      <ViewPODialog
        open={viewPODialog} onOpenChange={setViewPODialog} selectedPO={selectedPO} user={user}
        editing={editingPO} editItems={editPOItems} editSupplierName={editSupplierName} editNotes={editPONotes}
        onSetEditing={setEditingPO} onUpdateEditItem={updateEditPOItem} onRemoveEditItem={removeEditPOItem}
        onSupplierChange={setEditSupplierName} onNotesChange={setEditPONotes} onUpdate={handleUpdatePO}
        onApprove={handleApprovePO} onReject={handleRejectPO} onDelete={handleDeletePO} onExport={handleExportPO}
        canDownload={hasPerm("downloads_allowed")}
      />
    </div>
  );
};

export default Dashboard;
